/**
 * Por dónde se vino.
 *
 * «Volver» went where each screen said: a form to its list, a product's page to the products, a
 * client's to the clients, a tab to wherever its section was opened from. So from the home screen's
 * «Mercadería comprada» it opened «Compras», where nobody had been; from «Análisis» a product went
 * back to the products, and from «Fiado» a client to the clients. The owner (2026-10-08): the back
 * button should go back, not to a parent screen.
 *
 * Now «Volver» is the history's own step back, the one the phone's back button takes too, to the
 * screen as it was left: its tab, its view, its filters. Moving along a section's tabs, or changing
 * what a screen shows, takes the place of the step instead of adding one (`Pestanas`, the screens'
 * own `replace`), so «Volver» from any tab is still the way into the section. Only a screen with
 * nothing behind it on this page load, the first after the app opens or reloads, goes where the
 * screen says (`volverA`), and in its place: past a reload the history's step back would load the
 * whole app again.
 *
 * And a task ends where it began (the owner, the same day: «when i finish charging a customer for
 * an ORDER on the orders page it should take me back to the Orders page»). Finished, a form goes
 * back the way it came (`volver`), leaving no step that would open it again; a done screen that
 * names its next place goes back to it if it is behind, however far, and otherwise takes the done
 * screen's place (`irA`).
 */

import type { MouseEvent } from 'react';
import type { AnyRouter, RouterHistory } from '@tanstack/react-router';

/** A screen in the history, enough to know it again. */
export interface Lugar {
  pathname: string;
  href: string;
}

/** Where to go: an address, and for a list opened on one of its views, its search. */
export interface Destino {
  to: string;
  search?: Record<string, unknown>;
}

/**
 * The entries this page load has made in the tab's history, by their place in it. Entries from
 * before a reload are not here: going back to one loads the app again, so nothing goes there.
 */
export type Pila = Map<number, Lugar>;

export type Movimiento = 'PUSH' | 'REPLACE' | 'BACK' | 'FORWARD' | 'GO';

/**
 * Keeps `pila` up to date with one move through the history. A new screen drops whatever was
 * ahead of it, as the browser does; going back or forward changes no entry, only which is current.
 */
export function anotar(pila: Pila, indice: number, lugar: Lugar, movimiento: Movimiento): void {
  if (movimiento === 'PUSH') for (const i of [...pila.keys()]) if (i > indice) pila.delete(i);
  if (movimiento === 'PUSH' || movimiento === 'REPLACE' || !pila.has(indice)) pila.set(indice, lugar);
}

/** The screen right behind entry `indice`, if this page load went through it. */
export function detras(pila: Pila, indice: number): Lugar | undefined {
  return pila.get(indice - 1);
}

/** How many steps back the nearest screen at `pathname` is, through this page load's own entries. */
export function pasosHasta(pila: Pila, indice: number, pathname: string): number | undefined {
  for (let i = indice - 1; pila.has(i); i--) if (pila.get(i)!.pathname === pathname) return indice - i;
  return undefined;
}

const pila: Pila = new Map();
let enrutador: AnyRouter | undefined;

const indiceDe = (location: { state: unknown }): number =>
  Number((location.state as { __TSR_index?: number } | null)?.__TSR_index ?? 0);

const lugarDe = (location: { pathname: string; href: string }): Lugar => ({
  pathname: location.pathname,
  href: location.href,
});

export function seguirHistoria(router: AnyRouter): void {
  enrutador = router;
  const historia: RouterHistory = router.history;
  anotar(pila, indiceDe(historia.location), lugarDe(historia.location), 'PUSH');
  historia.subscribe(({ location, action }) => anotar(pila, indiceDe(location), lugarDe(location), action.type));
}

const aqui = (r: AnyRouter): number => indiceDe(r.history.location);

/** The screen this one was opened from, if this page load opened it. */
export function anterior(): Lugar | undefined {
  return enrutador ? detras(pila, aqui(enrutador)) : undefined;
}

/**
 * Settled on another screen: what `volver` and `irA` hand back, for whatever comes after (a lock, a
 * search to set). A move that never lands, a history the browser would not walk, gives up after
 * two seconds rather than hold that back for ever.
 */
function alLlegar(r: AnyRouter): Promise<void> {
  const desde = r.state.location.href;
  return new Promise((listo) => {
    const fin = () => {
      dejar();
      clearTimeout(reloj);
      listo();
    };
    const dejar = r.subscribe('onResolved', ({ toLocation }) => {
      if (toLocation.href !== desde) fin();
    });
    const reloj = setTimeout(fin, 2_000);
  });
}

/**
 * «Volver», and a finished task: one step back through the history, to the screen as it was. With
 * nothing behind on this page load, `alternativa`, in this screen's place.
 */
export function volver(alternativa: Destino): Promise<void> {
  const r = enrutador;
  if (!r) return Promise.resolve();
  if (detras(pila, aqui(r))) {
    const llega = alLlegar(r);
    r.history.back();
    return llega;
  }
  return r.navigate({ to: alternativa.to, search: alternativa.search ?? {}, replace: true } as never);
}

/**
 * A done screen's way on: back to `destino` if it is behind, so that what was finished is not left
 * to come back to; otherwise to it, in this screen's place (`enSuLugar`, a task's end) or after it.
 */
export function irA(destino: Destino, { enSuLugar = true }: { enSuLugar?: boolean } = {}): Promise<void> {
  const r = enrutador;
  if (!r) return Promise.resolve();
  const pasos = pasosHasta(pila, aqui(r), destino.to);
  if (pasos !== undefined) {
    const llega = alLlegar(r);
    r.history.go(-pasos);
    return llega;
  }
  return r.navigate({ to: destino.to, search: destino.search ?? {}, replace: enSuLugar } as never);
}

/** A click a link should leave to the browser: another tab, another window, a download. */
const aparte = (e: MouseEvent): boolean =>
  e.defaultPrevented || e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey;

/**
 * For «Volver»'s `onClick`: back through the history when there is a screen behind, instead of the
 * link's own address, which is where it goes when there is none.
 */
export function alVolver(e: MouseEvent): void {
  if (aparte(e) || !enrutador || !anterior()) return;
  e.preventDefault();
  enrutador.history.back();
}

/**
 * For a link's `onClick`: back through the history to where it points, when that is behind on this
 * page load, instead of opening it again on top. The house: home, with nothing to come back
 * through to what it left.
 */
export function atrasHasta(pathname: string): (e: MouseEvent) => void {
  return (e) => {
    const r = enrutador;
    if (aparte(e) || !r) return;
    const pasos = pasosHasta(pila, aqui(r), pathname);
    if (pasos === undefined) return;
    e.preventDefault();
    r.history.go(-pasos);
  };
}
